<template>
  <div ref="tableContainer" class="payroll-refunds-table">
    <DataTable
      ref="dataTableRef"
      data-testid="payroll-refunds-table"
      display-mode="handle_user_device"
      :config="tableConfig"
      :data="enrichedRefunds"
      :auto-load="false"
      :refreshButton="{ show: true }"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick"
      @refresh="$emit('refresh')"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateFormatter } from '../../../composables/useDateFormatter';
import { log } from '../../../utils/DebugLogger';

type Id = number | string;

interface Refund {
  id: Id;
  pays_to?: Id | null;
  user_id?: Id | null;
  title?: string;
  amount?: number | string;
  expense_date?: string | Date | null;
  created_at?: string | Date | null;
  file_url?: string;
  status?: number | string;
  blob_name?: string;
  [key: string]: unknown;
}

interface EnrichedRefund extends Refund {
  user_name: string;
  status_display: string;
  status_severity: string;
  expense_date_parsed: Date | null;
  created_at_parsed: Date | null;
  amount_display: number;
}

interface UserMapEntry {
  name?: string;
  nome?: string;
  [key: string]: unknown;
}

interface CellParams {
  value: unknown;
  data: EnrichedRefund;
}

interface ButtonClickPayload {
  action: string;
  data: EnrichedRefund;
}

interface GalleryPayload {
  fileUrl: string;
  title: string;
  refundId?: string | null;
}

interface Props {
  refunds?: Refund[];
  isLoading?: boolean;
  usersMap?: Record<Id, UserMapEntry>;
  showUser?: boolean;
  showSummary?: boolean;
  showAcceptDecline?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  refunds: () => [],
  isLoading: false,
  usersMap: () => ({}),
  showUser: true,
  showSummary: true,
  showAcceptDecline: false,
  canEdit: true,
  canDelete: true
});

const emit = defineEmits<{
  'edit': [refund: Refund];
  'view': [refund: EnrichedRefund];
  'accept': [refund: EnrichedRefund];
  'decline': [refund: EnrichedRefund];
  'delete': [refund: EnrichedRefund];
  'refresh': [];
  'view-gallery': [payload: GalleryPayload];
}>();

const { t, n } = useI18n();
const { formatDate: formatDateLocale, formatDateTime: formatDateTimeLocale, fromApiDateToDate } = useDateFormatter();

// Refs
const dataTableRef = ref<unknown>(null);
const tableContainer = ref<HTMLElement | null>(null);

// Helper functions
const getUserName = (userId: Id | null | undefined): string | null => {
  if (!userId || !props.usersMap) return null;
  const user = props.usersMap[userId];
  return user?.name || user?.nome || null;
};

interface StatusDisplay {
  label: string;
  severity: string;
}

const getStatusDisplay = (status: number | string | undefined): StatusDisplay => {
  switch (parseInt(String(status))) {
    case -1:
      return { label: t('Recusado'), severity: 'danger' };
    case 0:
      return { label: t('Pendente'), severity: 'warning' };
    case 1:
      return { label: t('Aceito'), severity: 'success' };
    default:
      return { label: t('Desconhecido'), severity: 'secondary' };
  }
};

// Computed: Enriched refunds data
const enrichedRefunds = computed((): EnrichedRefund[] => {
  return props.refunds.map((refund): EnrichedRefund => {
    const userName = getUserName(refund.pays_to) || getUserName(refund.user_id) || '-';
    const statusDisplay = getStatusDisplay(refund.status as number | string | undefined);

    // NOTE: expense_date uses fromApiDateToDate (noon local) to avoid timezone issues
    // where midnight UTC displays as previous day in GMT-3 (Brazil)
    const expenseDate = refund.expense_date instanceof Date
      ? refund.expense_date
      : refund.expense_date ? fromApiDateToDate(refund.expense_date as string) : null;

    const createdAt = refund.created_at instanceof Date
      ? refund.created_at
      : refund.created_at ? new Date(refund.created_at as string) : null;

    return {
      ...refund,
      user_name: userName,
      status_display: statusDisplay.label,
      status_severity: statusDisplay.severity,
      expense_date_parsed: expenseDate,
      created_at_parsed: createdAt,
      amount_display: parseFloat(String(refund.amount)) || 0
    };
  });
});

// Render functions
const renderStatus = ({ value, data }: CellParams): string => {
  const severity = data.status_severity || 'secondary';
  return `<span class="p-tag p-tag-${severity}">${value}</span>`;
};

const renderAmount = ({ data }: CellParams): string => {
  const amount = data.amount_display || 0;
  return `<span class="amount-value">${n(amount, 'currency')}</span>`;
};

const renderDate = ({ data }: CellParams): string => {
  const date = data.expense_date_parsed;
  if (!date || isNaN(date.getTime())) {
    return `<span class="text-muted">-</span>`;
  }
  return formatDateLocale(date);
};

const renderCreatedAt = ({ data }: CellParams): string => {
  const date = data.created_at_parsed;
  if (!date || isNaN(date.getTime())) {
    return `<span class="text-muted">-</span>`;
  }
  return formatDateTimeLocale(date);
};

const renderReceipt = ({ data }: CellParams): string => {
  if (data.file_url) {
    // Create thumbnail image that will open in gallery
    const title = data.title || t('Comprovante');
    const fileUrl = data.file_url;
    
    // Check if it's an image (jpg, jpeg, png, gif, webp)
    // Handle URLs with query parameters by checking before the '?' or '#'
    const urlWithoutParams = fileUrl.split('?')[0].split('#')[0];
    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(urlWithoutParams);
    
    if (isImage) {
      // For images, show thumbnail with clickable fallback
      return `
        <div class="receipt-thumbnail-cell" data-file-url="${fileUrl}" data-title="${title.replace(/"/g, '&quot;')}" data-refund-id="${data.id}">
          <img
            src="${fileUrl}"
            alt="${title.replace(/"/g, '&quot;')}"
            class="receipt-thumbnail-image"
            data-file-url="${fileUrl}"
            data-title="${title.replace(/"/g, '&quot;')}"
            data-refund-id="${data.id}"
            loading="lazy"
            onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
            title="${t('Clique para ampliar')}"
          />
          <div class="receipt-thumbnail-fallback" style="display: none;" data-file-url="${fileUrl}" data-title="${title.replace(/"/g, '&quot;')}" data-refund-id="${data.id}">
            <i class="pi pi-image"></i>
            <span>${t('Ver')}</span>
          </div>
        </div>
      `;
    } else {
      // For PDFs or other files, show link that also opens in gallery
      return `<a href="javascript:void(0)" class="receipt-link" data-file-url="${fileUrl}" data-title="${title.replace(/"/g, '&quot;')}"><i class="pi pi-paperclip"></i> ${t('Ver')}</a>`;
    }
  }
  return `<span class="text-muted">-</span>`;
};

// Status filter options
const statusFilterOptions = computed(() => [
  { label: t('Pendente'), value: t('Pendente') },
  { label: t('Aceito'), value: t('Aceito') },
  { label: t('Recusado'), value: t('Recusado') }
]);

// Table configuration
const tableConfig = computed(() => {
  const columns = [
    {
      data: 'id',
      title: t('ID'),
      renderType: 'clickable_id',
      minWidth: 70,
      sortable: true
    },
    {
      data: 'title',
      title: t('Título'),
      minWidth: 180,
      sortable: true
    },
    {
      data: 'amount_display',
      title: t('Valor'),
      minWidth: 100,
      sortable: true,
      cellRenderer: renderAmount
    },
    {
      data: 'expense_date_parsed',
      title: t('Data Despesa'),
      minWidth: 120,
      sortable: true,
      cellRenderer: renderDate
    },
    {
      data: 'status_display',
      title: t('Status'),
      minWidth: 100,
      sortable: true,
      filterType: 'select',
      filterOptions: statusFilterOptions.value,
      cellRenderer: renderStatus
    }
  ];

  // Add user column if showUser is true
  if (props.showUser) {
    columns.splice(2, 0, {
      data: 'user_name',
      title: t('Destinatário'),
      minWidth: 150,
      sortable: true
    });
  }

  // Add receipt column
  columns.push({
    data: 'file_url',
    title: t('Comprovante'),
    minWidth: 100,
    sortable: false,
    cellRenderer: renderReceipt
  });

  // Add created_at column
  columns.push({
    data: 'created_at_parsed',
    title: t('Criado em'),
    minWidth: 140,
    sortable: true,
    cellRenderer: renderCreatedAt
  });

  // Build actions array for the actions column
  const actions = [];

  // Edit button removed - using clickable_id instead

  if (props.showAcceptDecline) {
    actions.push({
      type: 'accept',
      label: t('Aceitar'),
      icon: 'pi pi-check',
      severity: 'success',
      showCondition: (row: EnrichedRefund) => parseInt(String(row.status)) === 0
    });
    actions.push({
      type: 'decline',
      label: t('Recusar'),
      icon: 'pi pi-times',
      severity: 'danger',
      showCondition: (row: EnrichedRefund) => parseInt(String(row.status)) === 0
    });
  }

  if (props.canDelete) {
    actions.push({
      type: 'delete',
      label: t('Excluir'),
      icon: 'pi pi-trash',
      severity: 'danger',
      showCondition: (row: EnrichedRefund) => parseInt(String(row.status)) === 0
    });
  }

  // View button for non-pending
  actions.push({
    type: 'view',
    label: t('Ver'),
    icon: 'pi pi-eye',
    showCondition: (row: EnrichedRefund) => parseInt(String(row.status)) !== 0
  });

  // Add actions column if there are any actions
  if (actions.length > 0) {
    columns.push({
      data: '',
      title: t('Ações'),
      minWidth: 150,
      sortable: false,
      renderType: 'action_buttons',
      buttons: actions.map(action => ({
        text: action.label,
        icon: action.icon,
        action: action.type,
        class: action.severity ? `p-button-${action.severity}` : '',
        condition: action.showCondition
      }))
    } as unknown as (typeof columns)[0]);
  }

  return {
    columns,
    actions,
    emptyMessage: t('Nenhuma solicitação de reembolso encontrada'),
    paginator: true,
    rows: 10,
    rowsPerPageOptions: [5, 10, 25, 50],
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.id;
      return id == null ? '' : `payroll-refunds-row-${id}`;
    }
  };
});

// Event handlers
const handleEditClick = (value: Id | EnrichedRefund): void => {
  log('PayrollRefundsTable', 'handleEditClick', 'Edit clicked', value);

  let refund: Refund | null = null;

  if (typeof value === 'number' || typeof value === 'string') {
    refund = enrichedRefunds.value.find(r => r.id === parseInt(String(value)) || r.id === value) ?? null;
    log('PayrollRefundsTable', 'handleEditClick', 'Found refund by ID', { id: value, refund });
  } else if (value && typeof value === 'object') {
    refund = value as EnrichedRefund;
  }

  if (refund) {
    emit('edit', refund);
  } else {
    log('PayrollRefundsTable', 'handleEditClick', 'Refund not found, emitting ID only', value);
    emit('edit', { id: value as Id });
  }
};

const handleButtonClick = ({ action, data }: ButtonClickPayload): void => {
  log('PayrollRefundsTable', 'handleButtonClick', 'Button clicked', { action, data });

  switch (action) {
    case 'edit':
      emit('edit', data);
      break;
    case 'view':
      emit('view', data);
      break;
    case 'accept':
      emit('accept', data);
      break;
    case 'decline':
      emit('decline', data);
      break;
    case 'delete':
      emit('delete', data);
      break;
  }
};

// Handle receipt clicks (both thumbnails and links) to open in gallery
const handleReceiptClick = (event: MouseEvent): void => {
  const target = event.target as HTMLElement;

  const thumbnailCell = target.closest('.receipt-thumbnail-cell') as HTMLElement | null;
  if (thumbnailCell) {
    event.preventDefault();
    event.stopPropagation();

    const fileUrl = thumbnailCell.getAttribute('data-file-url') ||
                    thumbnailCell.querySelector('.receipt-thumbnail-image')?.getAttribute('data-file-url') ||
                    thumbnailCell.querySelector('.receipt-thumbnail-fallback')?.getAttribute('data-file-url');
    const title = thumbnailCell.getAttribute('data-title') ||
                  thumbnailCell.querySelector('.receipt-thumbnail-image')?.getAttribute('data-title') ||
                  thumbnailCell.querySelector('.receipt-thumbnail-fallback')?.getAttribute('data-title') ||
                  t('Comprovante');
    const refundId = thumbnailCell.getAttribute('data-refund-id') ||
                     thumbnailCell.querySelector('.receipt-thumbnail-image')?.getAttribute('data-refund-id') ||
                     thumbnailCell.querySelector('.receipt-thumbnail-fallback')?.getAttribute('data-refund-id');

    if (fileUrl) {
      log('PayrollRefundsTable', 'handleReceiptClick', 'Opening receipt thumbnail in gallery', { fileUrl, title, refundId });
      emit('view-gallery', { fileUrl, title, refundId });
    }
    return;
  }

  const link = target.closest('.receipt-link') as HTMLElement | null;
  if (!link) return;

  event.preventDefault();
  event.stopPropagation();
  const fileUrl = link.getAttribute('data-file-url');
  const title = link.getAttribute('data-title') || t('Comprovante');

  if (fileUrl) {
    log('PayrollRefundsTable', 'handleReceiptClick', 'Opening receipt link in gallery', { fileUrl, title });
    emit('view-gallery', { fileUrl, title });
  }
};

// Lifecycle
onMounted(() => {
  log('PayrollRefundsTable', 'onMounted', 'Component mounted');
  
  // Use document-level event delegation to handle clicks on receipt thumbnails/links
  // This works even when DataTable re-renders rows
  document.addEventListener('click', handleReceiptClick);
});

onBeforeUnmount(() => {
  log('PayrollRefundsTable', 'onBeforeUnmount', 'Component unmounted');
  document.removeEventListener('click', handleReceiptClick);
});
</script>

<style scoped>
.payroll-refunds-table {
  width: 100%;
}

:deep(.amount-value) {
  font-weight: 600;
  color: var(--primary-600);
}

:deep(.receipt-link) {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: var(--primary-color);
  text-decoration: none;
}

:deep(.receipt-link:hover) {
  text-decoration: underline;
}

:deep(.receipt-thumbnail-cell) {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 5px;
  cursor: pointer;
}

:deep(.receipt-thumbnail-image) {
  max-height: 80px;
  max-width: 100%;
  height: auto;
  width: auto;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  object-fit: cover;
}

:deep(.receipt-thumbnail-image:hover) {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

:deep(.receipt-thumbnail-fallback) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 8px 12px;
  background-color: var(--surface-100);
  border-radius: 4px;
  cursor: pointer;
  color: var(--primary-color);
  font-size: 0.875rem;
  width: 100%;
  height: 100%;
  min-height: 60px;
}

:deep(.receipt-thumbnail-fallback:hover) {
  background-color: var(--surface-200);
}

:deep(.text-muted) {
  color: var(--text-color-secondary);
}
</style>
